import React, { FC, useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { Button, ValidationForm } from '../../atoms';
import styles from './CommoditiesAddOrganism.module.css';
import cn from 'classnames';
import { CustomDropdown, ImageUploader } from '../../molecules';
import Radio from '../../atoms/Radio';
import Spinner from '../../atoms/Spinner/Spinner';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { fetchCommoditiesNonRecursive } from '../../../store/tradeAPI/commodity/reducers/commodity.slice';
import { NonRecursiveCommodityResponseDto } from '../../../store/tradeAPI/commodity/dto/response/non-recursive-commodity-response.dto';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';

interface CommoditiesAddOrganismProps {
  handleSubmit: (commodity: NonRecursiveCommodityResponseDto) => void;
  commodity: NonRecursiveCommodityResponseDto;
  handleOnChange: (commodity: NonRecursiveCommodityResponseDto) => void;
  handleImageOnChange: (imageMeta: ImageMeta) => void;
}

interface DropDownItem {
  eventKey: string;
  label: string;
}

const CommoditiesAddOrganism: FC<CommoditiesAddOrganismProps> = ({
  handleSubmit,
  commodity,
  handleOnChange,
  handleImageOnChange,
}) => {
  const { nonRecursiveCommodities, nonRecursiveStatus } = useSelector(
    (state: RootState) => state.commoditySlice
  );
  const [dropDownItems, setDropDownItems] = useState<DropDownItem[]>();
  const [initialTitle, setInitialTitle] = useState<string>();

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(fetchCommoditiesNonRecursive());
  }, [dispatch]);

  useEffect(() => {
    if (nonRecursiveStatus === 'succeeded' && nonRecursiveCommodities) {
      setDropDownItems(
        nonRecursiveCommodities.map(
          (nonRecursiveCommodity: NonRecursiveCommodityResponseDto) => ({
            eventKey: nonRecursiveCommodity.id,
            label: nonRecursiveCommodity.name,
          })
        )
      );
    }
  }, [nonRecursiveCommodities, nonRecursiveStatus]);

  useEffect(() => {
    if (dropDownItems) {
      if (dropDownItems.length > 0) {
        setInitialTitle(
          dropDownItems.find(
            (dropDownItem) => dropDownItem.eventKey === commodity.parentId
          )?.label ?? 'Choose commodity'
        );
      } else {
        setInitialTitle('Choose commodity');
      }
    }
  }, [commodity.parentId, dropDownItems]);

  if (nonRecursiveStatus === 'loading' || !initialTitle) {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.commodityWrapper)}>
      <ValidationForm onSubmitCallback={() => handleSubmit(commodity)}>
        <Form.Group className="mb-3">
          <Form.Label>Name</Form.Label>
          <Form.Control
            required
            type="text"
            placeholder="Enter name"
            onChange={(e) =>
              handleOnChange({ ...commodity, name: e.target.value })
            }
            value={commodity.name}
          />
          <Form.Control.Feedback type="invalid">
            Please provide a valid name
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Parent</Form.Label>
          <CustomDropdown
            initialTitle={initialTitle}
            items={dropDownItems ?? []}
            onSelectOption={(e) =>
              handleOnChange({ ...commodity, parentId: e.eventKey })
            }
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Hs Code</Form.Label>
          <Form.Control
            required
            type="text"
            placeholder="Enter Hs Code"
            onChange={(e) =>
              handleOnChange({ ...commodity, hsCode: e.target.value })
            }
            value={commodity.hsCode}
          />
          <Form.Control.Feedback type="invalid">
            Please provide a valid hs code
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Qty Unit</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter Qty Unit"
            onChange={(e) =>
              handleOnChange({ ...commodity, qtyUnit: e.target.value })
            }
            value={commodity.qtyUnit ?? ''}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Time Unit</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter Time Unit"
            onChange={(e) =>
              handleOnChange({ ...commodity, timeUnit: e.target.value })
            }
            value={commodity.timeUnit ?? ''}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <ImageUploader
            setImage={(e) => {
              handleOnChange({ ...commodity, image: e });
            }}
            imageSrc={commodity.image ?? undefined}
            id={commodity.id}
            setImageMeta={(imageMeta) => handleImageOnChange(imageMeta)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Is Published</Form.Label>
          <Radio
            defaultChecked={commodity.published}
            onChange={(e) => handleOnChange({ ...commodity, published: e })}
          />
        </Form.Group>

        <Button variant="dark" type="submit">
          Save
        </Button>
      </ValidationForm>
    </div>
  );
};

export default CommoditiesAddOrganism;
